import type { VenueRatesClickSimpleFact } from '@/analytics/models/cventAnalytics/VenueRatesClickSimpleFact';
import { CSN_VENUE_RATES_SIMPLE_CLICK_TYPE, FACT_VERSION, PRODUCT } from '@/utils/analyticsClientHelper';
import type { VenueRate } from '@/models/VenueRate';
import { dateFormatter } from '@/utils/venueRatesHelper';
import type { MapView } from '@/constants/mapConstants';

export type EntityClickedProps = {
  entity_id?: string | null;
  entity_name?: string | null;
  click_target?: string | null;
  entity_type?: string | null;
  entity_position?: number | null;
};

type MetadataProps = {
  event_dates: string | null;
  historical: 0;
  view_mode: string;
  start_date: string | null;
  end_date: string | null;
  outside_the_range: number;
  state_code: string;
  country_code: string;
};
type PageDataProps = {
  page_name: string;
  page_type: null;
  product: typeof PRODUCT;
  page_id: null;
  search_text: string;
  search_type: string;
};
export default function getVenueRatesClickFact(
  entityListed: string[],
  entityClicked: EntityClickedProps,
  pageData: PageDataProps,
  mapView: MapView,
  metadata: MetadataProps,
  environment: string
): VenueRatesClickSimpleFact {
  return {
    type: CSN_VENUE_RATES_SIMPLE_CLICK_TYPE,
    entity_listed: JSON.stringify(entityListed),
    entity_clicked: JSON.stringify(entityClicked),
    page_data: JSON.stringify(pageData),
    meta_data: metadata ? JSON.stringify(metadata) : 'null',
    map_view: mapView,
    env: environment,
    ver: FACT_VERSION
  };
}

function getFormattedDate(eventStartDate: Date | undefined) {
  if (eventStartDate) {
    const eventYear = eventStartDate.getFullYear();
    const eventMonth = eventStartDate.getMonth() + 1;
    const eventDay = eventStartDate.getDate().toString().padStart(2, '0');
    return eventStartDate ? `${dateFormatter(eventYear, eventMonth)}-${eventDay}` : null;
  }
  return null;
}

function getMetadata(
  viewMode: number,
  startIndex: number,
  endIndex: number,
  isOutsideTheRange: boolean,
  stateCode: string,
  countryCode: string,
  futureRates?: VenueRate[],
  eventStartDate?: Date
): MetadataProps {
  const startDate = futureRates?.at(startIndex);
  const endDate = futureRates?.at(endIndex);

  return {
    event_dates: eventStartDate ? getFormattedDate(eventStartDate) : null,
    historical: 0, // no historical data in 2.0
    view_mode: viewMode ? 'chart' : 'table',
    start_date: startDate ? `${dateFormatter(startDate.year, startDate.month)}-02` : null,
    end_date: endDate ? `${dateFormatter(endDate.year, endDate.month)}-02` : null,
    outside_the_range: isOutsideTheRange ? 1 : 0,
    state_code: stateCode,
    country_code: countryCode
  };
}

function getEntityClicked(
  clickTarget: string,
  entityType?: string,
  entityId?: string,
  entityName?: string,
  entityPosition?: number
): EntityClickedProps {
  return {
    entity_id: entityId || null, // dgCityId
    entity_name: entityName || null, // dgCityName
    click_target: clickTarget || null,
    entity_type: entityType || null,
    entity_position: entityPosition || null
  };
}

function getPageData(page: string, searchTerm: string, addressType: string): PageDataProps {
  return {
    page_name: page,
    page_type: null,
    product: PRODUCT,
    page_id: null,
    search_text: searchTerm,
    search_type: addressType
  };
}

export { getMetadata, getEntityClicked, getPageData };
